import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Pencil, Download, Trash2, MoreHorizontal, FileText, FileType2, BookOpenText } from 'lucide-react';
import { type Book } from '../../../types';

interface BookCardProps {
  book: Book;
  isNewlyImported: boolean;
  onClick: () => void;
  onEdit: (book: Book) => void;
  onExport: (book: Book) => void;
  onExportText: (book: Book, format: 'txt' | 'md', encoding?: 'utf8' | 'gbk') => void;
  onExportDocx?: (book: Book) => void;
  onRead?: (book: Book) => void;
  onDelete: (book: Book) => void;
}

function formatWordCount(count: number): string {
  if (count >= 10000) {
    return `${(count / 10000).toFixed(1)}万字`;
  }
  return `${count.toLocaleString()}字`;
}

function getBookColor(title: string): string {
  const palette = [
    'linear-gradient(135deg, #5b6e8a 0%, #7e94b5 100%)',
    'linear-gradient(135deg, #8a6b5b 0%, #b5957e 100%)',
    'linear-gradient(135deg, #5b8a6b 0%, #7eb594 100%)',
    'linear-gradient(135deg, #6b5b8a 0%, #957eb5 100%)',
    'linear-gradient(135deg, #8a6b4b 0%, #b58e7e 100%)',
    'linear-gradient(135deg, #4b6b8a 0%, #7e95b5 100%)',
    'linear-gradient(135deg, #6b8a5b 0%, #95b57e 100%)',
    'linear-gradient(135deg, #8a4b6b 0%, #b57e95 100%)',
  ];
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = title.charCodeAt(i) + ((hash << 5) - hash);
  }
  return palette[Math.abs(hash) % palette.length];
}

function HoverInfoOverlay({ book, isVisible }: { book: Book; isVisible: boolean }) {
  const descRef = useRef<HTMLParagraphElement>(null);
  const [fontSize, setFontSize] = useState<number | null>(null);

  const fitText = useCallback(() => {
    const el = descRef.current;
    if (!el) return;
    let size = 14;
    el.style.fontSize = `${size}px`;
    while (el.scrollHeight > el.clientHeight && size > 8) {
      size -= 0.5;
      el.style.fontSize = `${size}px`;
    }
    setFontSize(size);
  }, []);

  useEffect(() => {
    if (!book.description) return;
    const timer = setTimeout(fitText, 0);
    return () => clearTimeout(timer);
  }, [book.description, fitText]);

  const createdDate = book.created_at
    ? new Date(book.created_at).toLocaleDateString()
    : '未知';

  return (
    <div className={`book-cover-hover-overlay${isVisible ? ' is-visible' : ''}`}>
      <div className="book-cover-hover-content">
        {book.description ? (
          <p
            ref={descRef}
            className="book-cover-hover-desc"
            style={fontSize ? { fontSize: `${fontSize}px` } : undefined}
          >
            {book.description}
          </p>
        ) : (
          <p className="book-cover-hover-desc book-cover-hover-empty">暂无简介</p>
        )}
        <p className="book-cover-hover-date">创建于 {createdDate}</p>
      </div>
    </div>
  );
}

export const BookCard = ({ book, isNewlyImported, onClick, onEdit, onExport, onExportText, onExportDocx, onRead, onDelete }: BookCardProps) => {
  const [showInfo, setShowInfo] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // 菜单脱离父容器渲染（portal 到 body）并自行定位。
  //
  // 为什么必须这样做：`.book-card-cover` 有 `overflow: hidden`（用于裁掉封面圆角外的内容），
  // 而菜单原本是 `bottom: 100%` 从封面底部向上展开 —— 菜单一长就超出封面顶部被**裁掉**。
  // 表现是"菜单项莫名少了几项"，且窗口高度不同（内置浏览器 vs Edge）时缺的项数还不一样。
  const [menuPos, setMenuPos] = useState<{ bottom: number; right: number } | null>(null);
  const firstChar = book.title ? book.title.charAt(0) : '?';

  const openMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    // 用 bottom 定位（而非 translateY）：展开动画里的 transform 不会干扰位置
    setMenuPos({
      bottom: window.innerHeight - rect.top + 6,
      right: window.innerWidth - rect.right,
    });
    setMenuOpen(true);
  };

  useEffect(() => {
    if (!menuOpen) {
      setShowInfo(false);
      setMenuPos(null);
      return;
    }
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    // 视口变化时关闭，避免菜单停留在旧坐标（定位用的是一次性快照）
    const onViewportChange = () => setMenuOpen(false);
    document.addEventListener('mousedown', onMouseDown);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [menuOpen]);

  return (
    <div className="book-card-wrapper">
      <div
        onClick={onClick}
        className={`book-card-cover ${isNewlyImported ? 'newly-imported' : ''}`}
      >
        <div className="book-card-cover-content">
          {book.cover ? (
            <img src={book.cover} alt={book.title} className="book-card-cover-image" />
          ) : (
            <div className="book-card-cover-placeholder" style={{ background: getBookColor(book.title) }}>
              <span className="book-card-cover-initial">{firstChar}</span>
            </div>
          )}
        </div>

        <HoverInfoOverlay book={book} isVisible={showInfo} />

        {/* 作品操作按钮 */}
        <div className="book-cover-menu-wrapper">
          <button
            ref={triggerRef}
            className="book-cover-menu-trigger"
            onClick={(e) => { e.stopPropagation(); if (menuOpen) setMenuOpen(false); else openMenu(); }}
            onMouseEnter={() => setShowInfo(true)}
            onMouseLeave={() => { if (!menuOpen) setShowInfo(false); }}
            title="作品操作"
          >
            <MoreHorizontal size={16} />
          </button>
          {menuOpen && menuPos && ReactDOM.createPortal(
            <div
              ref={menuRef}
              className="book-cover-menu-popup"
              style={{ position: 'fixed', bottom: menuPos.bottom, right: menuPos.right }}
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => { setMenuOpen(false); onEdit(book); }}>
                <Pencil size={13} />
                <span>编辑信息</span>
              </button>
              <button onClick={() => { setMenuOpen(false); onExportText(book, 'txt'); }}>
                <FileText size={13} />
                <span>导出 TXT</span>
              </button>
              {onExportDocx && (
                <button onClick={() => { setMenuOpen(false); onExportDocx(book); }}>
                  <FileType2 size={13} />
                  <span>导出 Word</span>
                </button>
              )}
              <div className="book-cover-menu-divider" />
              {/* 备份与前两项性质不同：.xts 是**可完整还原**的加密包（含资料库/设定/分卷/伏笔等），
                  必须设置密码；而 TXT/Word 只是正文，供阅读或发给他人，不加密。 */}
              <button onClick={() => { setMenuOpen(false); onExport(book); }}>
                <Download size={13} />
                <span>备份作品</span>
              </button>
              <div className="book-cover-menu-divider" />
              {onRead && (
                <button onClick={() => { setMenuOpen(false); onRead(book); }}>
                  <BookOpenText size={13} />
                  <span>阅读模式</span>
                </button>
              )}
              <div className="book-cover-menu-divider" />
              <button className="book-cover-menu-danger" onClick={() => { setMenuOpen(false); onDelete(book); }}>
                <Trash2 size={13} />
                <span>删除作品</span>
              </button>
            </div>,
            document.body,
          )}
        </div>
      </div>

      <div className="book-card-info">
        <h3 className="book-card-title" title={book.title}>{book.title}</h3>
        <div className="book-card-meta">
          <span className="book-card-word-count">{formatWordCount(book.word_count)}</span>
        </div>
      </div>
    </div>
  );
};
